import { useEffect, useRef } from 'react';
import { toaster } from 'baseui/toast';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loggerAtom } from '../../../modernization-common/logger/logger-atom';
import type { TrackObjectsOutputT, TrackObjectsRequestT } from '../../types';
import { annotationIdToLabelMapAtom } from '../atoms/annotation-atoms';
import { dispatchAtom } from '../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../atoms/label-store-dispatch/events';
import { mlTrackingJobsAtom } from '../atoms/ml-tracking-atoms';
import { trackAcrossKeyframes } from '../utils/track-across-keyframes';

// Toasts auto-dismiss; the studio `ToasterContainer` sets no global duration, so we pass one.
const TOAST_AUTO_HIDE_MS = 4000;
const getInitiatedMessage = (displayName: string) => `Tracking initiated for ${displayName}`;
const getCompletedMessage = (displayName: string) => `Tracking completed for ${displayName}`;
const getFailedMessage = (displayName: string) => `Tracking failed for ${displayName}`;
type UseMlTrackingJobRunnerArgsT = {
  // Task-scoped tracking capability; injected via APIContext (already bound to the task).
  trackObjects?: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
};

/**
 * Serial background runner for keyframe-stepper ML tracking jobs. Drains
 * `mlTrackingJobsAtom` one object at a time: for each job it fires a start toast,
 * issues a single batch tracking call via `trackAcrossKeyframes`, applies the
 * tracked frames by index (`UPDATE_TRACKING_ANNOTATION_FRAMES`), then fires a
 * completion (or failure) toast and pops the job so the next one runs.
 *
 * Mounted once in `EditorContainer`; inert while the queue is empty (flag off ⇒
 * nothing enqueues ⇒ this never runs). A `useRef` guard keeps execution serial
 * even as the effect re-runs on unrelated atom changes.
 */
export const useMlTrackingJobRunner = ({
  trackObjects
}: UseMlTrackingJobRunnerArgsT): void => {
  const [jobs, setJobs] = useAtom(mlTrackingJobsAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const annotationIdToLabelMap = useAtomValue(annotationIdToLabelMapAtom);
  const logger = useAtomValue(loggerAtom);
  const isRunningRef = useRef(false);
  useEffect(() => {
    // One object at a time; skip if we can't run the batch call.
    if (isRunningRef.current || jobs.length === 0 || !trackObjects) {
      return;
    }
    const job = jobs[0];
    if (!job) {
      return;
    }
    isRunningRef.current = true;
    const run = async () => {
      toaster.info(getInitiatedMessage(job.displayName), {
        key: `ml-tracking-initiated-${job.id}`,
        autoHideDuration: TOAST_AUTO_HIDE_MS
      });
      try {
        const annotationLabel = annotationIdToLabelMap.get(job.annotationId);
        if (!annotationLabel) {
          throw new Error(`No annotation label found for ${job.annotationId}`);
        }
        await trackAcrossKeyframes({
          job,
          trackObjects,
          annotationLabel,
          applyTrackedFrames: (annotationId, startFrame, endFrame, frameAnnotations) => dispatch({
            type: EVENTS.UPDATE_TRACKING_ANNOTATION_FRAMES,
            annotationIdx: annotationId,
            startFrame,
            endFrame,
            frameAnnotations
          }),
          logger
        });
        toaster.positive(getCompletedMessage(job.displayName), {
          key: `ml-tracking-completed-${job.id}`,
          autoHideDuration: TOAST_AUTO_HIDE_MS
        });
      } catch {
        // The seam already logs the underlying cause via `logger`; surface it to the user.
        toaster.negative(getFailedMessage(job.displayName), {
          key: `ml-tracking-failed-${job.id}`,
          autoHideDuration: TOAST_AUTO_HIDE_MS
        });
      } finally {
        // Clear the guard before popping so the effect re-run processes the next job.
        isRunningRef.current = false;
        setJobs(prev => prev.slice(1));
      }
    };
    void run();
  }, [jobs, trackObjects, annotationIdToLabelMap, dispatch, logger, setJobs]);
};